import {
  ANDROID_PLAY_STORE_URL,
  IOS_APP_STORE_URL,
} from '@/constants/appStores';
import { palette } from '@/theme/constants';
import { getMobileAppPlatform, MobileAppPlatform } from '@/utils/detectMobilePlatform';
import CloseIcon from '@mui/icons-material/Close';
import SmartphoneOutlinedIcon from '@mui/icons-material/SmartphoneOutlined';
import {
  Box,
  IconButton,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { styles } from './styles';

const BANNER_MESSAGE = 'Get the Memvy app for the best experience.';
const PROFILE_SETTINGS_PATH = '/app/profile';

const APP_LAYOUT_PATH_PREFIXES = ['/app/home', '/app/profile', '/app/collaborators', '/app/thankyou', '/app/story'];

const usesAppLayout = (pathname: string) =>
  pathname === '/404' || APP_LAYOUT_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));

export const MobileAppBanner = () => {
  const router = useRouter();
  const bannerRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(true);
  const [platform, setPlatform] = useState<MobileAppPlatform>('unknown');
  const isNarrowViewport = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const onAppLayoutPage = usesAppLayout(router.pathname);
  const isProfileSettingsPage = router.pathname === PROFILE_SETTINGS_PATH;
  const isBannerVisible = visible && !isProfileSettingsPage;

  useEffect(() => {
    setPlatform(getMobileAppPlatform());
  }, []);

  const syncBannerHeight = useCallback(() => {
    if (!isBannerVisible || !bannerRef.current) {
      document.documentElement.style.setProperty('--mobile-app-banner-height', '0px');
      return;
    }
    document.documentElement.style.setProperty(
      '--mobile-app-banner-height',
      `${bannerRef.current.offsetHeight}px`,
    );
  }, [isBannerVisible]);

  useEffect(() => {
    document.documentElement.classList.toggle('mobile-app-banner-active', isBannerVisible);
    syncBannerHeight();

    if (!isBannerVisible || !bannerRef.current) return;

    const observer = new ResizeObserver(syncBannerHeight);
    observer.observe(bannerRef.current);
    return () => observer.disconnect();
  }, [isBannerVisible, platform, isNarrowViewport, syncBannerHeight]);

  useEffect(() => {
    if (isBannerVisible && !onAppLayoutPage) {
      document.body.style.paddingTop = 'var(--mobile-app-banner-height, 0px)';
    } else {
      document.body.style.paddingTop = '';
    }

    return () => {
      document.body.style.paddingTop = '';
    };
  }, [isBannerVisible, onAppLayoutPage]);

  useEffect(() => {
    return () => {
      document.documentElement.style.removeProperty('--mobile-app-banner-height');
      document.documentElement.classList.remove('mobile-app-banner-active');
      document.body.style.paddingTop = '';
    };
  }, []);

  const dismiss = () => setVisible(false);

  const renderGooglePlayControl = (compact: boolean) => {
    return (
      <Box
        component='a'
        href={ANDROID_PLAY_STORE_URL}
        target='_blank'
        rel='noopener noreferrer'
        aria-label='Get Memvy on Google Play'
        sx={styles.storeLink(compact)}>
        {!compact && (
          <Box component='span' sx={styles.storeIcon}>
            <Image src='/images/google.svg' alt='' width={12} height={12} />
          </Box>
        )}
        {compact ? 'Play' : 'Google Play'}
      </Box>
    );
  };

  const renderStoreActions = () => {
    const compact = isNarrowViewport;

    if (platform === 'ios') {
      return (
        <Box
          component='a'
          href={IOS_APP_STORE_URL}
          target='_blank'
          rel='noopener noreferrer'
          aria-label='Install Memvy from the App Store'
          sx={styles.installButton}>
          <Typography variant='button' component='span' sx={styles.installLabel}>
            Install
          </Typography>
        </Box>
      );
    }

    if (platform === 'android') {
      return (
        <Box
          component='a'
          href={ANDROID_PLAY_STORE_URL}
          target='_blank'
          rel='noopener noreferrer'
          aria-label='Install Memvy from Google Play'
          sx={styles.installButton}>
          <Typography variant='button' component='span' sx={styles.installLabel}>
            Install
          </Typography>
        </Box>
      );
    }

    return (
      <>
        <Box
          component='a'
          href={IOS_APP_STORE_URL}
          target='_blank'
          rel='noopener noreferrer'
          aria-label='Download Memvy on the App Store'
          sx={styles.storeLink(compact)}>
          {!compact && (
            <Box component='span' sx={styles.storeIcon}>
              <Image src='/images/apple.svg' alt='' width={12} height={12} />
            </Box>
          )}
          {compact ? 'iOS' : 'App Store'}
        </Box>
        {renderGooglePlayControl(compact)}
      </>
    );
  };

  if (!isBannerVisible) return null;

  return (
    <Box ref={bannerRef} component='header' role='banner' sx={styles.header}>
      <Box sx={styles.inner}>
        <Box sx={styles.phoneIcon}>
          <SmartphoneOutlinedIcon sx={{ fontSize: 16, color: palette.primary }} />
        </Box>

        <Typography variant='body2' component='p' sx={styles.message}>
          {BANNER_MESSAGE}
        </Typography>

        <Box sx={styles.actions}>
          {renderStoreActions()}
          <IconButton
            size='small'
            onClick={dismiss}
            aria-label='Dismiss app download banner'
            sx={styles.dismissButton}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};
